import { _notNil } from '@/littledash';
import type { SendFilterChipOption } from '@/components/Agents/SendValidator/Report/SendFilterChips';
import type { Finding, SendSeverityCounts, SendSeverityLevel, SendSeveritySource, Severity } from '@/model/Agent.model';

/** Most serious first: the order findings sort in and severity chips read in. */
export const SEVERITY_LEVELS: ReadonlyArray<SendSeverityLevel> = ['reject', 'error', 'warning', 'notice', 'advisory'];

export const SEVERITY_LEVEL_TEXT: Record<SendSeverityLevel, string> = {
  reject: 'Reject',
  error: 'Error',
  warning: 'Warning',
  notice: 'Notice',
  advisory: 'Advisory',
};

/** The levels a check's own findings can carry, each with a server count. Advisory is Benchling In Vivo AI's alone. */
export const CHECK_SEVERITY_LEVELS: ReadonlyArray<keyof SendSeverityCounts> = ['reject', 'error', 'warning', 'notice'];

const LEVEL_OF_THREE_LEVEL_SEVERITY: Record<Severity, SendSeverityLevel> = {
  error: 'error',
  warning: 'warning',
  info: 'notice',
};

/**
 * A finding as stored. One stored before five-level severity has neither field, whatever the generated
 * type says, because adding them did not change the result's schema version.
 */
interface GradedFinding {
  severity: Severity;
  origin: Finding['origin'];
  severity_level?: SendSeverityLevel;
  severity_source?: SendSeveritySource;
}

/**
 * A finding's level of five. An older result has only the three-level severity, which reads as the same
 * level except info, which becomes notice. A Benchling In Vivo AI note is always advisory.
 */
export const severityLevelOf = (finding: GradedFinding): SendSeverityLevel =>
  finding.severity_level ?? (finding.origin === 'llm' ? 'advisory' : LEVEL_OF_THREE_LEVEL_SEVERITY[finding.severity]);

/** Sorts findings most serious first. */
export const bySeverityLevel = (a: GradedFinding, b: GradedFinding): number =>
  SEVERITY_LEVELS.indexOf(severityLevelOf(a)) - SEVERITY_LEVELS.indexOf(severityLevelOf(b));

/**
 * Whether a finding blocks a submission: only one a published grading rejects. Blocking is its own axis,
 * never read off a severity; a derived error is something to fix, not a statement that the FDA would
 * refuse the file. No published grading exists for SEND in M1, so nothing blocks.
 */
export const blocksSubmission = (finding: GradedFinding): boolean =>
  finding.severity_level === 'reject' && finding.severity_source === 'published';

/** A result or file as stored: one stored before the honest counts existed has none. */
interface CountedUnit {
  severity_counts?: SendSeverityCounts;
}

/** The checks' findings at each level, or nothing for a result stored before they were counted. */
export const severityCountsOf = (unit: CountedUnit): SendSeverityCounts | undefined => unit.severity_counts;

/** Who graded a finding, in the report's words. None of the three is a regulator's judgement unless published. */
export const SEVERITY_SOURCE_TEXT: Record<SendSeveritySource, { label: string; title: string }> = {
  published: { label: 'Published grade', title: 'Copied from a published grading of the rule' },
  derived: {
    label: 'Our grade',
    title: "Graded by our documented policy from the CDISC rule's published facts. Not a regulator's judgement",
  },
  in_house: { label: "Our check's grade", title: 'Our grade for one of our own checks' },
};

/** Who graded a finding, or nothing for one stored before that was recorded. */
export const severitySourceOf = (finding: GradedFinding): SendSeveritySource | undefined => finding.severity_source;

/** A finding as stored: one stored before FDA validator ids were recorded has none. */
interface FdaCitingFinding {
  fda_validator_rule_ids?: Array<string>;
}

/** A finding as stored: one stored before encodings were named has none, as has any no CDISC encoding produced. */
interface EncodedFinding {
  encoding_id?: string;
}

/** The CDISC encoding whose check produced a finding, or nothing. */
export const encodingIdOf = (finding: EncodedFinding): string | undefined => finding.encoding_id;

/** The FDA validator rules that check a finding's rule, or none. */
export const fdaValidatorIdsOf = (finding: FdaCitingFinding): Array<string> => finding.fda_validator_rule_ids ?? [];

/**
 * A chip for each level the checks found something at, carrying the server's count for it. Levels
 * with none are left out rather than shown as zeros. An older result has no counts that leave out
 * notices and AI notes, so its chips filter the levels on screen without numbers.
 */
export const severityChipsOf = (
  counts: SendSeverityCounts | undefined,
  checks: ReadonlyArray<GradedFinding>
): Array<SendFilterChipOption> => {
  if (_notNil(counts)) {
    return CHECK_SEVERITY_LEVELS.reduce<Array<SendFilterChipOption>>((chips, level) => {
      if (counts[level] > 0) {
        chips.push({ key: level, label: SEVERITY_LEVEL_TEXT[level], count: counts[level] });
      }
      return chips;
    }, []);
  }
  const shown = new Set(checks.map(severityLevelOf));
  return SEVERITY_LEVELS.reduce<Array<SendFilterChipOption>>((chips, level) => {
    if (shown.has(level)) {
      chips.push({ key: level, label: SEVERITY_LEVEL_TEXT[level] });
    }
    return chips;
  }, []);
};
